import { useEffect, useState } from "react";
import { ShoppingBag } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import ProductPurchase from "@/components/ProductPurchase";
import { formatXpf } from "@/components/XpfPrice";

/**
 * Produit lié à une vidéo, tel qu'exposé au client.
 * Volontairement limité aux colonnes lisibles côté client : jamais `file_url`.
 */
interface LinkedProduct {
  id: string;
  user_id: string;
  title: string;
  description: string | null;
  price: number;
  type: string;
  is_active: boolean;
  thumbnail_url: string | null;
  is_tama_catalog: boolean;
  service_type: string | null;
  service_delivery_method: string | null;
  service_duration: string | null;
  availability: string | null;
  profiles: {
    id: string;
    full_name: string | null;
    username: string | null;
    avatar_url: string | null;
  };
}

const PRODUCT_SELECT =
  "id, user_id, title, description, price, type, is_active, thumbnail_url, is_tama_catalog, service_type, service_delivery_method, service_duration, availability, profiles:user_id ( id, full_name, username, avatar_url )";

/** Cache mémoire : évite un refetch à chaque remontage du lecteur au scroll. */
const productCache = new Map<string, LinkedProduct | null>();

interface VideoProductCtaProps {
  productId: string;
  /** Auteur de la vidéo : le produit doit lui appartenir. */
  videoUserId: string;
  /** Le propriétaire consulte sa fiche en lecture seule (aucun paiement). */
  isOwner: boolean;
  /** Notifie le parent quand la fiche produit s'ouvre ou se ferme. */
  onOpenChange?: (open: boolean) => void;
}

/**
 * Appel à l'action d'achat affiché dans le lecteur quand la vidéo est liée
 * à un produit Pro actif du même auteur. Rail de paiement identique à la
 * marketplace (virement acheteur → vendeur Pro).
 */
export const VideoProductCta = ({ productId, videoUserId, isOwner, onOpenChange }: VideoProductCtaProps) => {
  const [product, setProduct] = useState<LinkedProduct | null>(productCache.get(productId) ?? null);
  const [open, setOpen] = useState(false);

  /** Notifie le parent à chaque changement d'état de la fiche produit. */
  useEffect(() => {
    onOpenChange?.(open);
  }, [open]);

  // Si le composant se démonte alors que le panneau est ouvert, on relâche
  // explicitement le verrou du feed.
  useEffect(() => {
    return () => {
      onOpenChange?.(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (productCache.has(productId)) {
      setProduct(productCache.get(productId) ?? null);
      return;
    }

    supabase
      .from("products")
      .select(PRODUCT_SELECT)
      .eq("id", productId)
      .eq("user_id", videoUserId)
      .eq("is_active", true)
      // Filet de diffusion : un produit non approuvé n'est jamais proposé.
      .eq("moderation_status", "approved")
      .eq("is_tama_catalog", false)
      .maybeSingle()
      .then(({ data, error }) => {
        // Aucun message technique à l'utilisateur : on n'affiche simplement rien.
        const result = error ? null : ((data as unknown as LinkedProduct) ?? null);
        productCache.set(productId, result);
        if (!cancelled) setProduct(result);
      });

    return () => {
      cancelled = true;
    };
  }, [productId, videoUserId]);

  if (!product) return null;

  return (
    <>
      <button
        type="button"
        aria-label={isOwner ? `Voir la fiche ${product.title}` : `Acheter ${product.title}`}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        className="mb-1 inline-flex items-center gap-1.5 rounded-full border border-player-border bg-player-surface/10 px-2.5 py-1 text-xs font-medium text-player-foreground hover:bg-player-surface/20 max-w-full"
      >
        <ShoppingBag className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        {isOwner ? (
          <span className="truncate">Acheter</span>
        ) : (
          <>
            <span className="truncate">Acheter</span>
            <span className="whitespace-nowrap opacity-90">{formatXpf(product.price)}</span>
          </>
        )}
      </button>

      {open && (
        <ProductPurchase
          product={product}
          open={open}
          onClose={() => setOpen(false)}
          readOnly={isOwner}
        />
      )}
    </>
  );
};

export default VideoProductCta;
